import { useContext, useState, useEffect } from "react";
import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { ThemeContext } from "../../../../theme/ThemeContext";
import AuthContext from "../../../../auth/AuthContext";
import { Skeleton } from "primereact";
import Moment from "moment";
import QuizService from "../../../../services/QuizService";

const QuizCalender = () => {
  const [date, setDate] = useState(new Date());
  const [quizEvents, setQuizEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const authContext = useContext(AuthContext);
  const tooltipBg = "#ffffff";
  const tooltipText = "#000000";
  const tooltipBorder = isDark ? "#555" : "#ddd";

  const tooltipStyles = `
    .tooltip-inner {
      background-color: ${tooltipBg} !important;
      color: ${tooltipText} !important;
      border: 1px solid ${tooltipBorder};
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      font-size: 10px;
    }
    .tooltip.bs-tooltip-top .tooltip-arrow::before {
      border-top-color: ${tooltipBg} !important;
    }
    .tooltip.bs-tooltip-bottom .tooltip-arrow::before {
      border-bottom-color: ${tooltipBg} !important;
    }
    .tooltip.bs-tooltip-left .tooltip-arrow::before {
      border-left-color: ${tooltipBg} !important;
    }
    .tooltip.bs-tooltip-right .tooltip-arrow::before {
      border-right-color: ${tooltipBg} !important;
    }
    .tooltip { opacity: 1 !important; }
  `;

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const quizColor = "#33A1E0";

  const getDaysInMonth = (year, month) => {
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let i = 1; i <= totalDays; i++) cells.push(i);
    return cells;
  };

  const changeMonth = (offset) => {
    setDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + offset);
      return newDate;
    });
  };

useEffect(() => {
    const controller = new AbortController(); 

    const fetchCalendarData = async () => {
      setLoading(true);
      try {
        const startDate = Moment(
          new Date(date.getFullYear(), date.getMonth(), 1)
        ).format("YYYY-MM-DD");
        const endDate = Moment(
          new Date(date.getFullYear(), date.getMonth() + 1, 0)
        ).format("YYYY-MM-DD");

        const response = await QuizService.GetCalender(
          authContext,
          startDate,
          endDate,
          controller.signal 
        );

        if (controller.signal.aborted) return; 

        if (response && response.isSuccess && Array.isArray(response.data)) {
          const quizzes = response.data.filter(
            (event) => event.type?.toLowerCase() === "quiz"
          );
          setQuizEvents(quizzes);
        } else {
          setQuizEvents([]);
        }
      } catch (error) {
        if (error.name === "AbortError") return; 
        console.error("Failed to fetch calendar data:", error);
        setQuizEvents([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false); 
      }
    };

    fetchCalendarData();

    return () => controller.abort(); 
  }, [authContext, date]);

  function getDayQuizHighlight(dateObj) {
    const iso = dateObj.toISOString().split("T")[0];
    const dayQuizzes = quizEvents.filter((quiz) => {
      const plannedDateStr = quiz.plannedStartDate || quiz.plannedEndDate;
      if (!plannedDateStr) return false;
      const plannedDateISO = new Date(plannedDateStr).toISOString().split("T")[0];
      return iso === plannedDateISO;
    });

    if (dayQuizzes.length > 0) {
      return {
        bgColor: quizColor,
        tooltipData: dayQuizzes.map((quiz) => renderQuizTooltip(quiz)),
      };
    }
    return { bgColor: null, tooltipData: [] };
  }

  function renderQuizTooltip(quiz) {
    return (
      <div key={`tooltip-${quiz.id}`} style={{ fontSize: 11, minWidth: 120 }}>
        <div
          style={{
            fontWeight: 600,
            color: quizColor,
            textTransform: "capitalize",
            marginBottom: 2,
          }}
        >
          {quiz.name}
        </div>
        <div style={{ color: "#666" }}>
          Date: {quiz.strPlannedStartDate || ""}
        </div>
      </div>
    );
  }

  const cells = getDaysInMonth(date.getFullYear(), date.getMonth());

  return (
    <>
      <style>{tooltipStyles}</style>
      <Card
        className="p-3 charts"
        style={{
          height: "337px",
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          border: "1px solid #eee",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-2 px-1 gap-2">
          <div style={{ minWidth: 100 }}>
            {loading ? (
              <Skeleton width="100%" height="30px" />
            ) : (
              <div
                style={{
                  color: isDark ? "#eaeaea" : "#000",
                  padding: "4px 8px",
                }}
              >
                Quiz Calendar
              </div>
            )}
          </div>

          <button
            className="btn btn-light btn-sm py-0 px-1 dropdown"
            onClick={() => changeMonth(-1)}
            disabled={loading}
          >
            &lt;
          </button>
          <h6 style={{ marginBottom: 0, minWidth: 75, textAlign: "center" }}>
            {loading ? (
              <Skeleton width="60px" height="20px" />
            ) : (
              `${monthNames[date.getMonth()]} ${date.getFullYear()}`
            )}
          </h6>
          <button
            className="btn btn-light btn-sm py-0 px-1 dropdown"
            onClick={() => changeMonth(1)}
            disabled={loading}
          >
            &gt;
          </button>
        </div>

        {loading ? (
          <div
            style={{
              flexGrow: 1,
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "6px",
            }}
          >
            {days.map((_, i) => (
              <Skeleton key={`day-skel-${i}`} width="100%" height="20px" />
            ))}
            {Array.from({ length: 35 }).map((_, i) => (
              <Skeleton key={`cell-skel-${i}`} width="100%" height="30px" />
            ))}
          </div>
        ) : (
          <div
            className="calendar-grid text-center"
            style={{
              flexGrow: 1,
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "6px",
              fontSize: 12,
              height: "100%",
            }}
          >
            {days.map((day) => (
              <div
                key={day}
                className="fw-bold"
                style={{ color: isDark ? "#eaeaea" : "#333", fontSize: 11 }}
              >
                {day}
              </div>
            ))}

            {cells.map((cell, idx) => {
              if (!cell) return <div key={idx} style={{ height: "32" }}></div>;

              const fullDate = new Date(date.getFullYear(), date.getMonth(), cell);
              const { bgColor, tooltipData } = getDayQuizHighlight(fullDate);

              const textColor = bgColor ? "#fff" : isDark ? "#eaeaea" : "#000";

              return (
                <OverlayTrigger
                  key={idx}
                  placement="top"
                  overlay={
                    tooltipData.length > 0 ? (
                      <Tooltip id={`tooltip-${idx}`}>
                        {tooltipData}
                      </Tooltip>
                    ) : (
                      <Tooltip id={`tooltip-${idx}`} style={{ display: "none" }}>
                        <div></div>
                      </Tooltip>
                    )
                  }
                >
                  <div
                    className="calendar-cell rounded"
                    style={{
                      backgroundColor: bgColor || "transparent",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      height: 36,
                      color: textColor,
                      border: "none",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      padding: "2px 4px",
                    }}
                  >
                    <div>{cell}</div>
                  </div>
                </OverlayTrigger>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
};

export default QuizCalender;
